import React, { useState, useEffect } from "react";
// Chakra imports
import {
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Link,
  Switch,
  Text,
  useColorModeValue,
  Image,
  useToast,
  FormErrorMessage,
} from "@chakra-ui/react";
// Assets
import signInImage from "assets/img/signInImage.jpg";
import logo from "assets/img/logo.png";
// API Services
import { authService } from "api";

// Auth utilities
import { redirectIfAuthenticated } from "utils/auth";

function SignIn() {
  // Chakra color mode
  const textColor = useColorModeValue("gray.700", "white");
  const bgForm = useColorModeValue("white", "navy.800");
  const titleColor = useColorModeValue("gray.700", "blue.500");
  
  // State management
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('admin'); // 'admin' | 'company'
  
  // Chakra hooks
  const toast = useToast();
  
  // Check if user is already authenticated
  useEffect(() => {
    redirectIfAuthenticated();
  }, []);
  
  // Handle login
  const handleLogin = async () => {
    // Reset errors
    setErrors({});
    
    // Basic validation
    if (!email.trim()) {
      setErrors({ email: "البريد الإلكتروني مطلوب" });
      return;
    }
    
    if (!password.trim()) {
      setErrors({ password: "كلمة المرور مطلوبة" });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const loginFn = activeTab === 'admin' ? authService.adminLogin : authService.companyLogin;
      const result = await loginFn(email, password);
       
       if (result.success) {
         // Store the bearer token
         const token = result.data.data.token;
         const adminName = result.data.data.user.name || `${result.data.data.user.first_name} ${result.data.data.user.last_name}`;
         
         // Store in localStorage
         localStorage.setItem('auth_token', token);
         localStorage.setItem('admin_name', adminName);
         localStorage.setItem('user_data', JSON.stringify(result.data.data.user));
         
         toast({
           title: "تم تسجيل الدخول بنجاح",
           description: result.message,
           status: "success",
           duration: 2000,
           isClosable: true,
         });
         
         // Redirect to dashboard after a short delay
         setTimeout(() => {
           window.location.href = '#/admin/dashboard';
         }, 1000);
         
       } else {
         
         // Handle different error types
         if (result.errorType === 'validation') {
           setErrors({
             email: result.errorMessages.find(msg => msg.includes('البريد الإلكتروني')) ? "البريد الإلكتروني غير صحيح" : "",
             password: result.errorMessages.find(msg => msg.includes('كلمة المرور')) ? "كلمة المرور غير صحيحة" : ""
           });
         }
         
         toast({
           title: "خطأ في تسجيل الدخول",
           description: result.message,
           status: "error",
           duration: 5000,
           isClosable: true,
         });
       }
       } catch (error) {
         toast({
           title: "خطأ في النظام",
           description: "حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى",
           status: "error",
           duration: 5000,
           isClosable: true,
         });
       } finally {
         setIsLoading(false);
       }
  };
  
  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      w="100vw"
      h="100vh"
      overflow="hidden"
      dir="rtl"
    >
      {/* Background Image */}
      <Box
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        bgImage={signInImage}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        zIndex="1"
      >
        <Box
          w="100%"
          h="100%"
          bg="blue.500"
          opacity="0.8"
        />
      </Box>
      
      {/* Sign In Form */}
      <Flex
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        alignItems="center"
        justifyContent="center"
        zIndex="2"
        direction="column"
        gap="14px"
      >
        {/* Logo */}
        <Flex justify="center">
          <Image
            src={logo}
            alt="الشعار"
            w="120px"
            h="auto"
            objectFit="contain"
          />
        </Flex>

        {/* Selector on top of the card */}
        <Flex
          bg={bgForm}
          borderRadius="9999px"
          p="6px"
          gap="6px"
          boxShadow={useColorModeValue(
            "0px 5px 14px rgba(0, 0, 0, 0.05)",
            "unset"
          )}
        >
          <Button
            size="sm"
            variant={activeTab === 'admin' ? 'solid' : 'ghost'}
            colorScheme="blue"
            onClick={() => setActiveTab('admin')}
          >
            المشرف
          </Button>
          <Button
            size="sm"
            variant={activeTab === 'company' ? 'solid' : 'ghost'}
            colorScheme="blue"
            onClick={() => setActiveTab('company')}
          >
            الشركة
          </Button>
        </Flex>

        {/* Flip Card Container */}
        <Box style={{ perspective: '1000px' }}>
          <Box
            position="relative"
            w="445px"
            style={{ transformStyle: 'preserve-3d', transition: 'transform 0.6s', transform: activeTab === 'admin' ? 'rotateY(0deg)' : 'rotateY(180deg)' }}
          >
            {/* Front - Admin */}
            <Flex
              direction="column"
              w="445px"
              background="transparent"
              borderRadius="15px"
              p="40px"
              bg={bgForm}
              boxShadow={useColorModeValue(
                "0px 5px 14px rgba(0, 0, 0, 0.05)",
                "unset"
              )}
              style={{ backfaceVisibility: 'hidden' }}
            >
              <Text
                fontSize="xl"
                color={textColor}
                fontWeight="bold"
                textAlign="center"
                mb="22px"
              >
                تسجيل دخول المشرف
              </Text>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal">
                  البريد الإلكتروني
                </FormLabel>
                <Input
                  variant="auth"
                  fontSize="sm"
                  ms="4px"
                  type="email"
                  placeholder="أدخل بريدك الإلكتروني"
                  mb="24px"
                  size="lg"
                  textAlign="right"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <FormErrorMessage ms="4px">{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal">
                  كلمة المرور
                </FormLabel>
                <Input
                  variant="auth"
                  fontSize="sm"
                  ms="4px"
                  type="password"
                  placeholder="أدخل كلمة المرور"
                  mb="24px"
                  size="lg"
                  textAlign="right"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <FormErrorMessage ms="4px">{errors.password}</FormErrorMessage>
              </FormControl>

              <FormControl display="flex" alignItems="center" mb="24px">
                <Switch id="remember-login-admin" colorScheme="blue" me="10px" />
                <FormLabel htmlFor="remember-login-admin" mb="0" fontWeight="normal">
                  تذكرني
                </FormLabel>
              </FormControl>

              <Button
                fontSize="10px"
                variant="dark"
                fontWeight="bold"
                w="100%"
                h="45"
                mb="24px"
                onClick={handleLogin}
                isLoading={isLoading}
                loadingText="جاري تسجيل الدخول..."
              >
                تسجيل الدخول
              </Button>
            </Flex>

            {/* Back - Company */}
            <Flex
              direction="column"
              w="445px"
              background="transparent"
              borderRadius="15px"
              p="40px"
              bg={bgForm}
              boxShadow={useColorModeValue(
                "0px 5px 14px rgba(0, 0, 0, 0.05)",
                "unset"
              )}
              position="absolute"
              top="0"
              left="0"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <Text
                fontSize="xl"
                color={textColor}
                fontWeight="bold"
                textAlign="center"
                mb="22px"
              >
                تسجيل دخول الشركة
              </Text>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal">
                  البريد الإلكتروني
                </FormLabel>
                <Input
                  variant="auth"
                  fontSize="sm"
                  ms="4px"
                  type="email"
                  placeholder="أدخل بريدك الإلكتروني"
                  mb="24px"
                  size="lg"
                  textAlign="right"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <FormErrorMessage ms="4px">{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel ms="4px" fontSize="sm" fontWeight="normal">
                  كلمة المرور
                </FormLabel>
                <Input
                  variant="auth"
                  fontSize="sm"
                  ms="4px"
                  type="password"
                  placeholder="أدخل كلمة المرور"
                  mb="24px"
                  size="lg"
                  textAlign="right"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <FormErrorMessage ms="4px">{errors.password}</FormErrorMessage>
              </FormControl>

              <FormControl display="flex" alignItems="center" mb="24px">
                <Switch id="remember-login-company" colorScheme="blue" me="10px" />
                <FormLabel htmlFor="remember-login-company" mb="0" fontWeight="normal">
                  تذكرني
                </FormLabel>
              </FormControl>

              <Button
                fontSize="10px"
                variant="dark"
                fontWeight="bold"
                w="100%"
                h="45"
                mb="24px"
                onClick={handleLogin}
                isLoading={isLoading}
                loadingText="جاري تسجيل الدخول..."
              >
                تسجيل الدخول
              </Button>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default SignIn;
